/**
 * Discover Page - Landing page for different user types
 * Routes users to appropriate SEO pages based on their needs
 */
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Building2,
  HardHat,
  Home,
  ArrowRight,
  CheckCircle2,
  Users,
  Briefcase,
  Shield,
  Phone
} from "lucide-react";

const userTypes = [
  {
    id: "business",
    title: "For Bedrifter",
    subtitle: "Entreprenører & Byggefirmaer",
    description: "Komplette stillasløsninger for byggeprosjekter. Vi leverer fra planlegging til ferdig montert stillas.",
    icon: Building2,
    color: "from-[#4F46E5] to-[#7C3AED]",
    borderColor: "border-[#4F46E5]/30 hover:border-[#4F46E5]",
    link: "/stillasfirma-oslo",
    features: [
      "Prosjektering av stillas",
      "HAKI-stillas montering",
      "HMS og sikkerhet",
      "Fleksibel kapasitet"
    ],
    cta: "Få tilbud",
    badge: "B2B"
  },
  {
    id: "worker",
    title: "For Stillasmontører",
    subtitle: "Jobb som Stillasmontør",
    description: "Bli med på laget! Vi søker erfarne stillasmontører og lag. Konkurransedyktige betingelser og fast arbeid.",
    icon: HardHat,
    color: "from-[#23F7DD] to-[#06B6D4]",
    borderColor: "border-[#23F7DD]/30 hover:border-[#23F7DD]",
    link: "/stillasjobb-oslo",
    features: [
      "Konkurransedyktig lønn",
      "Faste oppdrag",
      "Profesjonelt miljø",
      "HMS i fokus"
    ],
    cta: "Søk jobb",
    badge: "Karriere"
  },
  {
    id: "private",
    title: "For Private",
    subtitle: "Stillas til Bolig",
    description: "Trenger du stillas til hus eller fasade? Vi leverer trygge og fleksible løsninger til private boliger.",
    icon: Home,
    color: "from-[#F7931A] to-[#FBBF24]",
    borderColor: "border-[#F7931A]/30 hover:border-[#F7931A]",
    link: "/stillas-privat-oslo",
    features: [
      "Fasadearbeid",
      "Maling & rehabilitering",
      "Takarbeid",
      "Rask levering"
    ],
    cta: "Få pris",
    badge: "Privat"
  }
];

export default function DiscoverPage() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Våre Tjenester | AllStillas - Stillas i Oslo</title>
        <meta name="description" content="AllStillas tilbyr stillastjenester for bedrifter, private og jobbmuligheter for stillasmontører i Oslo og Viken." />
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1">
            <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
              <span className="text-[#23F7DD] font-bold text-base font-['Unbounded']">All</span>
            </div>
            <span className="text-lg font-bold font-['Unbounded'] text-white">Stillas</span>
          </Link>
          
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:text-[#23F7DD]">
                Logg inn
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">
                Registrer
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-white font-['Unbounded'] mb-6">
            Hva kan vi hjelpe deg med?
          </h1>
          <p className="text-lg md:text-xl text-[#94A3B8] mb-4">
            Velg det som passer deg best, så viser vi deg mer informasjon.
          </p>
        </div>
      </section>

      {/* User Type Cards */}
      <section className="px-4 pb-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {userTypes.map((type) => {
            const Icon = type.icon;
            return (
              <Card 
                key={type.id}
                className={`bg-[#0A0A0A] border-2 ${type.borderColor} transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl group overflow-hidden`}
              >
                <CardContent className="p-0">
                  {/* Card Header with gradient */}
                  <div className={`bg-gradient-to-br ${type.color} p-6 relative`}>
                    <span className="absolute top-4 right-4 px-3 py-1 text-xs font-bold bg-white/20 backdrop-blur-sm rounded-full text-white">
                      {type.badge}
                    </span>
                    <Icon className="w-12 h-12 text-white mb-4" />
                    <h2 className="text-2xl font-bold text-white font-['Unbounded']">{type.title}</h2>
                    <p className="text-white/80 text-sm mt-1">{type.subtitle}</p>
                  </div>
                  
                  {/* Card Body */}
                  <div className="p-6 space-y-6">
                    <p className="text-[#94A3B8]">{type.description}</p>
                    
                    {/* Features */}
                    <ul className="space-y-2">
                      {type.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-white">
                          <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    {/* CTA Button */}
                    <Link to={type.link} className="block">
                      <Button 
                        className={`w-full bg-gradient-to-r ${type.color} text-white hover:opacity-90 font-bold py-6 group-hover:shadow-lg transition-all`}
                      >
                        {type.cta}
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Quick Contact */}
      <section className="px-4 pb-20">
        <div className="max-w-2xl mx-auto text-center">
          <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
            <Phone className="w-8 h-8 text-[#23F7DD] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Usikker på hva du trenger?</h3>
            <p className="text-[#94A3B8] mb-4">Ring oss, så hjelper vi deg videre.</p>
            <a href="tel:+4793922819" className="text-2xl font-bold text-[#23F7DD] hover:underline block mb-2">
              +47 9392 2819
            </a>
            <a href="mailto:support@allstillas.no" className="text-[#94A3B8] hover:text-[#23F7DD] transition-colors">
              support@allstillas.no
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-1">
              <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded">
                <span className="text-[#23F7DD] font-bold text-sm font-['Unbounded']">All</span>
              </div>
              <span className="text-sm font-bold font-['Unbounded'] text-white">Stillas</span>
            </Link>
            <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#23F7DD]/10 border border-[#23F7DD]/20 rounded-full">
              <span className="text-[#23F7DD] text-[10px]">⚡</span>
              <span className="text-[10px] text-[#94A3B8]">Powered by</span>
              <span className="text-[10px] text-[#23F7DD]">Doserne AS</span>
            </div>
          </div>
          <p className="text-sm text-[#64748B]">© 2026 Doserne AS. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
